import axios from 'axios';

const API_URL = 'https://api-pos.minimarket116.kz/api/v1';

// Создание экземпляра axios с базовым URL
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Генерация UUID для корреляции и идемпотентности
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

// Функция для повторных попыток с экспоненциальной задержкой
const retryWithDelay = async (fn, maxRetries = 5, baseDelay = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            if (attempt === maxRetries || !error.message.includes('Ошибка сети')) {
                throw error;
            }

            const delay = baseDelay * Math.pow(2, attempt - 1);
            console.log(`Попытка ${attempt} неудачна, повторяем через ${delay}мс`);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
};

// Функции для работы с токенами устройства
const deviceTokenManager = {
    // Получение access token из localStorage
    getAccessToken: () => localStorage.getItem('device_access_token'),

    // Получение refresh token из localStorage
    getRefreshToken: () => localStorage.getItem('device_refresh_token'),

    // Получение времени истечения токена
    getTokenExpiration: () => {
        const expiresAt = localStorage.getItem('device_access_token_expires_at');
        return expiresAt ? parseInt(expiresAt, 10) : null;
    },

    // Установка токенов
    setTokens: (accessToken, refreshToken, expiresIn) => {
        localStorage.setItem('device_access_token', accessToken);
        localStorage.setItem('device_refresh_token', refreshToken);

        // Сохраняем время истечения токена, если передан expiresIn
        if (expiresIn) {
            const expiresAt = Date.now() + expiresIn * 1000; // Переводим секунды в миллисекунды
            localStorage.setItem('device_access_token_expires_at', expiresAt.toString());
        }
    },

    // Очистка токенов
    clearTokens: () => {
        localStorage.removeItem('device_access_token');
        localStorage.removeItem('device_refresh_token');
        localStorage.removeItem('device_access_token_expires_at');
        localStorage.removeItem('device_auth_code');
        localStorage.removeItem('device_cashier_info');
        localStorage.removeItem('device_register_info');
    },

    // Проверка, истечет ли токен в течение указанного времени (в миллисекундах)
    willTokenExpireSoon: (timeThreshold = 5 * 60 * 1000) => { // По умолчанию 5 минут
        const expiresAt = deviceTokenManager.getTokenExpiration();
        if (!expiresAt) return true; // Если нет данных о времени истечения, считаем, что токен скоро истечет

        const timeUntilExpiration = expiresAt - Date.now();
        return timeUntilExpiration <= timeThreshold;
    }
};

// Флаг для отслеживания выполнения рефреша токена
let isRefreshing = false;
// Очередь запросов, ожидающих обновления токена
let failedQueue = [];

// Функция для обработки очереди запросов
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Обновление токена
const refreshAuthToken = async () => {
    try {
        const refreshToken = deviceTokenManager.getRefreshToken();
        if (!refreshToken) {
            throw new Error('Refresh token not available');
        }

        const response = await axios.post(`${API_URL}/device/auth/refresh`, {
            refresh_token: refreshToken
        }, {
            headers: {
                'Content-Type': 'application/json',
                'Idempotency-Key': generateUUID(),
                'X-Correlation-Key': generateUUID()
            }
        });

        if (response.data.status === 'success' && response.data.data) {
            const { access_token, refresh_token, expires_in } = response.data.data;
            deviceTokenManager.setTokens(access_token, refresh_token, expires_in);
            return access_token;
        } else {
            throw new Error('Failed to refresh token');
        }
    } catch (error) {
        console.error('Ошибка при обновлении токена:', error);
        // Проверяем, это сетевая ошибка или ошибка авторизации
        if (error.response?.status === 401 || error.response?.status === 403) {
            // Только при явной ошибке авторизации выбрасываем исключение
            throw error;
        }
        // Для сетевых ошибок тоже выбрасываем, но они будут обработаны по-другому
        throw error;
    }
};

// Интерцептор для добавления токена авторизации
api.interceptors.request.use(
    (config) => {
        const accessToken = deviceTokenManager.getAccessToken();
        if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Интерцептор для обработки ответов
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Если ошибка не 401 или это уже повторный запрос после рефреша, просто отклоняем
        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        // Если уже идет процесс обновления токена, добавляем запрос в очередь
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({resolve, reject});
            })
                .then(token => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return api(originalRequest);
                })
                .catch(err => {
                    return Promise.reject(err);
                });
        }

        // Начинаем процесс обновления токена
        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const newToken = await refreshAuthToken();
            processQueue(null, newToken);
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            isRefreshing = false;
            return api(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError, null);
            isRefreshing = false;

            // Очищаем токены и редиректим ТОЛЬКО при явных ошибках авторизации от сервера
            // НЕ редиректим при сетевых ошибках, таймаутах или других проблемах без ответа сервера
            const isNetworkError = !refreshError.response ||
                                   refreshError.code === 'ECONNABORTED' ||
                                   refreshError.code === 'ETIMEDOUT' ||
                                   refreshError.code === 'ERR_NETWORK';

            if (!isNetworkError &&
                (refreshError.response.status === 401 || refreshError.response.status === 403)) {
                deviceTokenManager.clearTokens();
                window.location.href = '/device/login';
            }

            return Promise.reject(refreshError);
        }
    }
);

// Сервис для авторизации устройства
export const deviceAuthService = {
    // Запрос кода авторизации
    requestAuthCode: async () => {
        try {
            const response = await axios.get(`${API_URL}/device/auth/request`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Idempotency-Key': generateUUID(),
                    'X-Correlation-Key': generateUUID()
                }
            });

            if (response.data.status === 'success' && response.data.data.auth_code) {
                localStorage.setItem('device_auth_code', response.data.data.auth_code);
                return response.data.data;
            } else {
                throw new Error('Не удалось получить код авторизации');
            }
        } catch (error) {
            // Различаем сетевые ошибки и ошибки API
            const message = error.response?.data?.message || (error.request ? 'Ошибка сети' : 'Ошибка при запросе кода авторизации');
            throw new Error(message);
        }
    },

    // Проверка статуса авторизации
    checkAuthStatus: async (authCode) => {
        try {
            const response = await axios.post(`${API_URL}/device/auth/status`, {
                auth_code: authCode
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Idempotency-Key': generateUUID(),
                    'X-Correlation-Key': generateUUID()
                }
            });

            if (response.data.status === 'success') {
                const { status, access_token, refresh_token, expires_in } = response.data.data;

                if (status === 'authorized' && access_token && refresh_token) {
                    deviceTokenManager.setTokens(access_token, refresh_token, expires_in);
                    localStorage.removeItem('device_auth_code');
                }

                return response.data.data;
            } else {
                throw new Error('Ошибка при проверке статуса авторизации');
            }
        } catch (error) {
            const message = error.response?.data?.message || (error.request ? 'Ошибка сети' : 'Ошибка при проверке статуса авторизации');
            throw new Error(message);
        }
    },

    // Выход из системы
    logout: async () => {
        try {
            const accessToken = deviceTokenManager.getAccessToken();
            if (!accessToken) {
                deviceTokenManager.clearTokens();
                return { success: true };
            }

            await axios.post(`${API_URL}/device/auth/logout`, {
                access_token: accessToken
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Idempotency-Key': generateUUID(),
                    'X-Correlation-Key': generateUUID()
                }
            });

            deviceTokenManager.clearTokens();
            return { success: true };
        } catch (error) {
            console.error('Ошибка при выходе:', error);
            // В случае ошибки все равно очищаем токены
            deviceTokenManager.clearTokens();
            return { success: true };
        }
    },

    // Получение сохраненного кода авторизации
    getSavedAuthCode: () => {
        return localStorage.getItem('device_auth_code');
    },

    // Проверка аутентификации
    isAuthenticated: () => {
        return !!deviceTokenManager.getAccessToken() && !!deviceTokenManager.getRefreshToken();
    }
};

// Сервис для работы с кассирами
export const deviceCashierService = {
    // Авторизация кассира
    loginCashier: async (cashierUuid, correlationKey, idempotencyKey) => {
        try {
            const headers = {
                'Content-Type': 'application/json'
            };
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post('/device/cashier/login', {
                cashier_uuid: cashierUuid
            }, { headers });

            if (response.data.status === 'success' && response.data.data) {
                const { access_token, refresh_token, expires_in } = response.data.data;
                // Обновляем токены после авторизации кассира
                deviceTokenManager.setTokens(access_token, refresh_token, expires_in);
                window.dispatchEvent(new CustomEvent('cashierStatusUpdate'));
                return response.data.data;
            }

            throw new Error('Не удалось авторизовать кассира');
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при авторизации кассира');
        }
    },

    // Выход кассира
    logoutCashier: async (cashierUuid, correlationKey, idempotencyKey) => {
        try {
            const headers = {
                'Content-Type': 'application/json'
            };
            if (correlationKey) headers['X-Correlation-Key'] = correlationKey;
            if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

            const response = await api.post('/device/cashier/logout', {
                cashier_uuid: cashierUuid
            }, { headers });

            if (response.data.status === 'success' && response.data.data) {
                const { access_token, refresh_token, expires_in } = response.data.data;
                // Обновляем токены после выхода кассира
                deviceTokenManager.setTokens(access_token, refresh_token, expires_in);
                // Очищаем информацию о кассире
                localStorage.removeItem('device_cashier_info');
                window.dispatchEvent(new CustomEvent('cashierStatusUpdate'));
                return response.data.data;
            }

            throw new Error('Не удалось выполнить выход кассира');
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при выходе кассира');
        }
    },

    // Получение информации о кассире
    getCashierStatus: async () => {
        try {
            const response = await api.get('/device/cashier/status');

            if (response.data.status === 'success') {
                // Сохраняем информацию о кассире
                localStorage.setItem('device_cashier_info', JSON.stringify(response.data.data));
                return response.data.data;
            }

            throw new Error('Не удалось получить информацию о кассире');
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении информации о кассире');
        }
    },

    // Получение сохраненной информации о кассире
    getSavedCashierInfo: () => {
        const cashierInfo = localStorage.getItem('device_cashier_info');
        return cashierInfo ? JSON.parse(cashierInfo) : null;
    }
};

// Сервис для работы с информацией об устройстве
export const deviceRegisterService = {
    // Получение информации об устройстве/кассе
    getRegisterInfo: async () => {
        try {
            const response = await api.get('/device/register/info');

            if (response.data.status === 'success') {
                // Сохраняем информацию об устройстве
                localStorage.setItem('device_register_info', JSON.stringify(response.data.data));
                return response.data.data;
            }

            throw new Error('Не удалось получить информацию об устройстве');
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении информации об устройстве');
        }
    },

    // Получение сохраненной информации об устройстве
    getSavedRegisterInfo: () => {
        const registerInfo = localStorage.getItem('device_register_info');
        return registerInfo ? JSON.parse(registerInfo) : null;
    },

    // Получение информации об устройстве/кассе
    getRegisterProducts: async () => {
        try {
            const response = await api.get('/device/register/products');

            if (response.data.status === 'success') {
                return response.data;
            }

            throw new Error('Не удалось получить информацию о продуктах');
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении информации о продуктах');
        }
    },

    // Получение инфо о смене по ID
    getShiftInfo: async (cashRegisterId) => {
        try {
            const response = await api.get(`/device/register/shift-info/${cashRegisterId}`);
            return response.data;
        } catch (error) {
            // В случае ошибки возвращаем объект с result: 'error'
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при получении информации о смене'
            };
        }
    },
};

// Сервис для работы с трекингом наличных
export const deviceCashTracking = {
    // Получение информации о сессии
    getSessionInfo: async () => {
        try {
            const response = await api.get('/device/cash-tracking');
            return response.data;
        } catch (error) {
            // Если это ошибка 400 с кодом no_active_session, возвращаем данные как есть
            if (error.response?.status === 400 && error.response?.data?.code === 'no_active_session') {
                return error.response.data;
            }
            // Для всех остальных ошибок бросаем исключение
            throw error;
        }
    },

    // Получение информации форм дата
    getCashTrackingFormData: async () => {
        try {
            const response = await api.get('/device/cash-tracking/form-data');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении информации');
        }
    },

    // Start session
    startSession: async (openingActual, correlationKey, idempotencyKey) => {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'X-Correlation-Key': correlationKey || generateUUID(),
                'Idempotency-Key': idempotencyKey || generateUUID()
            };

            const response = await api.post('/device/cash-tracking/start', {
                opening_actual: openingActual
            }, { headers });

            // Возвращаем весь response.data как есть
            return response.data;
        } catch (error) {
            // Если есть ответ от сервера, возвращаем его
            if (error.response?.data) {
                return error.response.data;
            }
            // Если нет ответа от сервера, бросаем исключение
            throw new Error('Ошибка сети при старте сессии');
        }
    },

    // Close session
    closeSession: async (openingActual, note, correlationKey, idempotencyKey) => {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'X-Correlation-Key': correlationKey || generateUUID(),
                'Idempotency-Key': idempotencyKey || generateUUID()
            };

            const response = await api.post('/device/cash-tracking/close', {
                amount_counted: openingActual,
                note: note
            }, { headers });

            // Возвращаем весь response.data как есть
            return response.data;
        } catch (error) {
            // Если есть ответ от сервера, возвращаем его
            if (error.response?.data) {
                return error.response.data;
            }
            // Если нет ответа от сервера, бросаем исключение
            throw new Error('Ошибка сети при закрытии сессии');
        }
    },

    // Create transactions
    createTransactions: async (amount, type, note, correlationKey, idempotencyKey) => {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'X-Correlation-Key': correlationKey || generateUUID(),
                'Idempotency-Key': idempotencyKey || generateUUID()
            };

            const response = await api.post('/device/cash-tracking/transaction', {
                amount: amount,
                type: type,
                note: note
            }, { headers });

            // Возвращаем весь response.data как есть
            return response.data;
        } catch (error) {
            // Если есть ответ от сервера, возвращаем его
            if (error.response?.data) {
                return error.response.data;
            }
            // Если нет ответа от сервера, бросаем исключение
            throw new Error('Ошибка сети при создании транзакции');
        }
    },
};

// Сервис для работы с sale return
export const deviceSaleReturn = {
    // Получение prepare по ID
    getPrepare: async (saleId) => {
        try {
            const response = await api.get(`/device/returns/prepare/${saleId}`);
            return response.data;
        } catch (error) {
            // В случае ошибки возвращаем объект с result: 'error'
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при получении чека'
            };
        }
    },

    // Создание return
    createReturn: async (returnData, correlationKey, idempotencyKey) => {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'X-Correlation-Key': correlationKey || generateUUID(),
                'Idempotency-Key': idempotencyKey || generateUUID()
            };

            const response = await api.post('/device/returns', returnData, { headers });

            // Возвращаем весь response.data как есть
            return response.data;
        } catch (error) {
            // Если есть ответ от сервера, возвращаем его
            if (error.response?.data) {
                return error.response.data;
            }
            // Если нет ответа от сервера, бросаем исключение
            throw new Error('Ошибка сети при создании возврата');
        }
    },
};

// Сервис для работы с продажами
export const deviceSalesService = {
    // Создание продажи
    createSale: async (saleData, idempotencyKey = null) => {
        const finalIdempotencyKey = idempotencyKey || generateUUID();
        const correlationKey = generateUUID();

        return await retryWithDelay(async () => {
            try {
                const headers = {
                    'Content-Type': 'application/json',
                    'X-Correlation-Key': correlationKey,
                    'Idempotency-Key': finalIdempotencyKey
                };

                const response = await api.post('/device/sales/sale', saleData, {
                    headers,
                    timeout: 30000 // 30 сек таймаут
                });

                return response.data;
            } catch (error) {
                if (error.response?.data) {
                    return error.response.data;
                }
                throw new Error('Ошибка сети при создании продажи');
            }
        });
    },

    // Получение чека по ID
    getReceipt: async (receiptId) => {
        try {
            const response = await api.get(`/device/sales/receipt/${receiptId}`);
            return response.data;
        } catch (error) {
            // В случае ошибки возвращаем объект с result: 'error'
            return {
                result: 'error',
                message: error.response?.data?.message || 'Ошибка при получении чека'
            };
        }
    },

    // Получить список продаж с пагинацией и поиском
    getSales: async (params = {}) => {
        try {
            const { limit = 20, cursor = null, search = '' } = params;
            const queryParams = new URLSearchParams();

            queryParams.append('limit', limit);
            if (cursor) {
                queryParams.append('cursor', cursor);
            }
            if (search) {
                queryParams.append('search', search);
            }

            const response = await api.get(`/device/sales?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Ошибка при получении списка продаж');
        }
    },

    // Получить данные продажи по ID
    getSaleById: async (id) => {
        try {
            const response = await api.get(`/device/sales/${id}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Ошибка при получении данных продажи';
            throw error;
        }
    },

    // Получить данные продажи по ID
    getCostumerByCardNumber: async (cardNumber) => {
        try {
            const response = await api.get(`/device/sales/client/${cardNumber}`);
            return response.data;
        } catch (error) {
            error.message = error.response?.data?.message || 'Такой клиент не найден';
            throw error;
        }
    },
};

// Сервис для печати чеков
export const devicePrintService = {
    // Печать чека через локальный сервис
    printReceipt: async (receiptHtml) => {
        try {
            const response = await fetch('http://127.0.0.1:3001/api/printReceipt', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ html: receiptHtml })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Ошибка печати чека');
            }

            return data;
        } catch (error) {
            console.error('Ошибка при печати чека:', error);
            throw new Error(error.message || 'Не удалось отправить чек на печать');
        }
    }
};

// Сервис для работы с ролями кассира
export const deviceCashierRoleService = {
    // Получение роли текущего кассира
    getCashierRole: () => {
        const cashierInfo = deviceCashierService.getSavedCashierInfo();
        return cashierInfo?.cashier?.role || null;
    },

    // Проверка конкретной роли
    hasRole: (role) => {
        const currentRole = deviceCashierRoleService.getCashierRole();
        return currentRole === role;
    },

    // Проверка наличия одной из ролей
    hasAnyRole: (roles) => {
        const currentRole = deviceCashierRoleService.getCashierRole();
        return roles.includes(currentRole);
    },

    // Проверка авторизации кассира
    isCashierAuthorized: () => {
        const cashierInfo = deviceCashierService.getSavedCashierInfo();
        return cashierInfo?.cashier_authorized === true;
    },

    // Получение полной информации о кассире
    getCashierInfo: () => {
        const cashierInfo = deviceCashierService.getSavedCashierInfo();
        return cashierInfo?.cashier || null;
    }
};

// Экспорт основного API экземпляра
export default {
    api
};